import React, { useState, useRef, useCallback, useEffect } from 'react';

/**
 * BeforeAfterSlider - Drag to compare original photo vs generated character.
 * @param {string} beforeSrc - Original photo URL
 * @param {string} afterSrc - Generated character URL
 * @param {string} beforeLabel - Label for original (default: "Original")
 * @param {string} afterLabel - Label for result (default: "Character")
 */
export function BeforeAfterSlider({ beforeSrc, afterSrc, beforeLabel = 'Original', afterLabel = 'Character' }) {
  const containerRef = useRef(null);
  const [position, setPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const updatePosition = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(5, Math.min(95, (x / rect.width) * 100));
    setPosition(pct);
  }, []);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    updatePosition(e.clientX);
  }, [updatePosition]);

  const handleTouchStart = useCallback((e) => {
    setIsDragging(true);
    updatePosition(e.touches[0].clientX);
  }, [updatePosition]);

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      updatePosition(clientX);
    };
    const handleEnd = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove);
    window.addEventListener('touchend', handleEnd);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, updatePosition]);

  if (!beforeSrc || !afterSrc) return null;

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className="relative w-full aspect-square rounded-xl overflow-hidden cursor-col-resize select-none"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      style={{ border: '1px solid rgba(255,255,255,0.08)' }}
    >
      {/* After image (full background) */}
      <img
        src={afterSrc}
        alt={afterLabel}
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />

      {/* Before image (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${position}%` }}
      >
        <img
          src={beforeSrc}
          alt={beforeLabel}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: `${containerRef.current?.offsetWidth || 500}px`, maxWidth: 'none' }}
          draggable={false}
        />
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 z-10"
        style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
      >
        <div className="w-[2px] h-full" style={{ background: '#c8f060' }} />
        {/* Handle */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full flex items-center justify-center"
          style={{
            background: '#c8f060',
            boxShadow: '0 0 20px rgba(200,240,96,0.4)',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 8L1 5M4 8L1 11M4 8H12M12 8L15 5M12 8L15 11" stroke="#080808" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-3 left-3 z-20">
        <span className="px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider"
          style={{ background: 'rgba(0,0,0,0.7)', color: 'rgba(245,240,232,0.7)', backdropFilter: 'blur(4px)' }}>
          {beforeLabel}
        </span>
      </div>
      <div className="absolute top-3 right-3 z-20">
        <span className="px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider"
          style={{ background: 'rgba(0,0,0,0.7)', color: '#c8f060', backdropFilter: 'blur(4px)' }}>
          {afterLabel}
        </span>
      </div>
    </div>
  );
}
